import { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Stack,
  TextInput,
  Button,
  Text,
  UnstyledButton,
  Group,
  Alert,
  Avatar,
  Select,
  Loader,
  Badge,
  Checkbox,
  Switch,
} from "@mantine/core";
import { User, Check, X } from "lucide-react";
import { useAuth } from "@/contexts";
import { getMyEngines, checkDisplayNameAvailability, type Engine } from "@/lib/api";
import { HexIcon } from "@/components/HexIcon";
import { IconPickerModal } from "@/components/Editor/IconPickerModal";
import type { EngineDefinition } from "@/types/engine";
import { useDebouncedCallback } from "@mantine/hooks";
import classes from "./ProfileModal.module.css";

interface ProfileModalProps extends React.ComponentPropsWithoutRef<typeof Modal> {
  opened: boolean;
  onClose: () => void;
}

export function ProfileModal({ opened, onClose, ...modalProps }: ProfileModalProps) {
  const { user, updateUser } = useAuth();
  const [displayName, setDisplayName] = useState("");
  const [selectedIcon, setSelectedIcon] = useState<string | null>(null);
  const [defaultEngineId, setDefaultEngineId] = useState<string | null>(null);
  const [defaultEditorEngineId, setDefaultEditorEngineId] = useState<string | null>(null);
  const [acceptTerms, setAcceptTerms] = useState<boolean | undefined>(false);
  const [hexNewsletterOptIn, setHexNewsletterOptIn] = useState<boolean | undefined>(false);
  const [dvdaNewsletterOptIn, setDvdaNewsletterOptIn] = useState<boolean | undefined>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Display name availability checking
  const [displayNameChecking, setDisplayNameChecking] = useState(false);
  const [displayNameAvailable, setDisplayNameAvailable] = useState<boolean | null>(null);
  const [displayNameMessage, setDisplayNameMessage] = useState<string>("");

  // Icon picker modal
  const [iconPickerOpen, setIconPickerOpen] = useState(false);

  // User's engines for default engine selection
  const [engines, setEngines] = useState<Engine[]>([]);
  const [enginesLoading, setEnginesLoading] = useState(false);

  // Debounced display name availability check
  const checkDisplayName = useDebouncedCallback(async (name: string) => {
    if (!name || name.trim().length === 0) {
      setDisplayNameAvailable(null);
      setDisplayNameMessage("");
      return;
    }

    // Don't check if it's the same as current display name
    if (name.trim() === user?.displayName) {
      setDisplayNameAvailable(true);
      setDisplayNameMessage("");
      return;
    }

    setDisplayNameChecking(true);
    const { data, error } = await checkDisplayNameAvailability(name.trim());
    setDisplayNameChecking(false);

    if (error) {
      setDisplayNameAvailable(null);
      setDisplayNameMessage("Could not check availability");
    } else if (data) {
      setDisplayNameAvailable(data.available);
      setDisplayNameMessage(data.message);
    }
  }, 500);

  // Handle display name change
  const handleDisplayNameChange = useCallback((value: string) => {
    setDisplayName(value);
    setDisplayNameAvailable(null);
    setDisplayNameMessage("");
    checkDisplayName(value);
  }, [checkDisplayName]);

  // Initialize form when modal opens
  useEffect(() => {
    if (opened && user) {
      setDisplayName(user.displayName || "");
      setSelectedIcon(user.avatarUrl || null);
      setDefaultEngineId(user.defaultEngineId || null);
      setDefaultEditorEngineId(user.defaultEditorEngineId || null);
      setAcceptTerms(user.acceptTerms);
      setHexNewsletterOptIn(user.hexNewsletterOptIn);
      setDvdaNewsletterOptIn(user.dvdaNewsletterOptIn);

      setError(null);
      setSuccess(false);

      // Reset display name validation
      setDisplayNameAvailable(null);
      setDisplayNameMessage("");
      setDisplayNameChecking(false);

      // Load user's engines
      setEnginesLoading(true);

      getMyEngines(true).then(({ data, error: apiError }) => {
        if (data && !apiError) {
          setEngines(data);
        }

        setEnginesLoading(false);
      });
    }
  }, [opened, user]);

  const handleSave = async () => {
    // Check if display name is available before saving
    if (displayNameAvailable === false) {
      setError("Please choose a different display name");
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(false);

    const { error: updateError } = await updateUser({
      displayName: displayName.trim(),
      avatarIcon: selectedIcon,
      defaultEngineId: defaultEngineId,
      defaultEditorEngineId: defaultEditorEngineId,
      acceptTerms: acceptTerms,
      hexNewsletterOptIn: hexNewsletterOptIn,
      dvdaNewsletterOptIn: dvdaNewsletterOptIn,
    });

    if (updateError) {
      setError(updateError);
    } else {
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1000);
    }

    setIsLoading(false);
  };

  const handleIconSelect = (iconName: string | null) => {
    setSelectedIcon(iconName);
    setIconPickerOpen(false);
  };

  // Build engine options for select
  const engineOptions = engines.map((engine) => {
    const def = engine.definition as EngineDefinition;
    return {
      value: engine.id,
      label: def.name,
    };
  });

  return (
    <>
      <Modal mt="md" opened={opened} onClose={onClose} title="Edit Profile" size="md" {...modalProps}>
        <Stack gap="lg">
          {error && (
            <Alert color="red" variant="light">
              {error}
            </Alert>
          )}

          {success && (
            <Alert color="green" variant="light">
              Profile updated successfully!
            </Alert>
          )}

          {/* Preview */}
          <Group justify="center">
            <Stack align="center" gap="xs">
              <UnstyledButton
                onClick={() => setIconPickerOpen(true)}
                className={classes.avatarButton}
                title="Click to change avatar"
              >
                <Avatar size="xl" color="violet" radius="xl">
                  {selectedIcon ? <HexIcon icon={selectedIcon} size={32} /> : <User size={32} />}
                </Avatar>
                <Badge size="xs" className={classes.editBadge}>
                  Edit
                </Badge>
              </UnstyledButton>
              <Text fw={500}>{displayName || user?.email?.split("@")[0] || "User"}</Text>
              <Text size="xs" c="dimmed">
                {user?.email}
              </Text>
            </Stack>
          </Group>

          {/* Display Name */}
          <TextInput
            label="Display Name"
            description="This is how you'll appear to others (must be unique)"
            placeholder={user?.email?.split("@")[0] || "Enter a display name"}
            value={displayName}
            onChange={(e) => handleDisplayNameChange(e.currentTarget.value)}
            maxLength={50}
            rightSection={
              displayNameChecking ? (
                <Loader size="xs" />
              ) : displayNameAvailable === true && displayName.trim() !== user?.displayName ? (
                <Check size={16} color="var(--mantine-color-green-6)" />
              ) : displayNameAvailable === false ? (
                <X size={16} color="var(--mantine-color-red-6)" />
              ) : null
            }
            error={displayNameAvailable === false ? displayNameMessage : undefined}
          />

          {/* Avatar Icon */}
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Avatar Icon
            </Text>
            <Group gap="sm">
              <Avatar size="md" color="violet" radius="xl">
                {selectedIcon ? <HexIcon icon={selectedIcon} size={20} /> : <User size={20} />}
              </Avatar>
              <Button variant="light" size="xs" onClick={() => setIconPickerOpen(true)}>
                {selectedIcon ? "Change Icon" : "Choose Icon"}
              </Button>
              {selectedIcon && (
                <Button variant="subtle" size="xs" color="red" onClick={() => setSelectedIcon(null)}>
                  Clear
                </Button>
              )}
            </Group>
            {selectedIcon && (
              <Text size="xs" c="dimmed">
                Current: {selectedIcon}
              </Text>
            )}
          </Stack>

          {/* Default Engine */}
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Default Engine
            </Text>
            <Text size="xs" c="dimmed">
              This engine will load automatically when you visit the site
            </Text>
            {enginesLoading ? (
              <Group gap="xs">
                <Loader size="xs" />
                <Text size="sm" c="dimmed">
                  Loading your engines...
                </Text>
              </Group>
            ) : engines.length === 0 ? (
              <Text size="sm" c="dimmed">
                No saved engines. Create one in the Editor to set as default.
              </Text>
            ) : (
              <Select
                placeholder="Use built-in engine"
                data={engineOptions}
                value={defaultEngineId}
                onChange={setDefaultEngineId}
                clearable
                searchable
              />
            )}
          </Stack>

          {/* Default Editor Engine */}
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Default Editor Engine
            </Text>
            <Text size="xs" c="dimmed">
              This engine will load automatically when you open the editor (optional)
            </Text>
            {enginesLoading ? (
              <Group gap="xs">
                <Loader size="xs" />
                <Text size="sm" c="dimmed">
                  Loading your engines...
                </Text>
              </Group>
            ) : engines.length === 0 ? (
              <Text size="sm" c="dimmed">
                No saved engines. Create one in the Editor to set as default.
              </Text>
            ) : (
              <Select
                placeholder="No default (start fresh)"
                data={engineOptions}
                value={defaultEditorEngineId}
                onChange={setDefaultEditorEngineId}
                clearable
                searchable
              />
            )}
          </Stack>

          <Stack gap="xs">
            <Switch
              label="Subscribe to Hex newsletter"
              checked={hexNewsletterOptIn}
              onChange={(e) => setHexNewsletterOptIn(e.currentTarget.checked)}
            />
            <Switch
              label="Subscribe to DVDA Games newsletter"
              checked={dvdaNewsletterOptIn}
              onChange={(e) => setDvdaNewsletterOptIn(e.currentTarget.checked)}
            />
            <Text size="xs" c="dimmed">
              You can opt-out at any time.
            </Text>
          </Stack>

          {!user?.acceptTerms ? (
            <Checkbox
              label={
                <>
                  I accept the{" "}
                  <a href="/terms" target="_blank" rel="noopener noreferrer">
                    terms of service
                  </a>
                </>
              }
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.currentTarget.checked)}
            />
          ) : null}

          {/* Actions */}
          <Group justify="flex-end" gap="sm">
            {modalProps.withCloseButton ? (
              <Button variant="subtle" onClick={onClose} disabled={isLoading}>
                Cancel
              </Button>
            ) : null}
            <Button
              onClick={handleSave}
              loading={isLoading}
              disabled={displayNameAvailable === false || displayNameChecking}
            >
              Save Changes
            </Button>
          </Group>
        </Stack>
      </Modal>

      <IconPickerModal
        opened={iconPickerOpen}
        onClose={() => setIconPickerOpen(false)}
        onSelect={handleIconSelect}
        currentIcon={selectedIcon}
      />
    </>
  );
}

export default ProfileModal;
